import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Brain, ArrowRight, Heart } from 'lucide-react';
import { fetchPublicPsychologyConsultations, toggleLikeSessionApi } from './humanPsychologyApi';
import { toast } from 'react-hot-toast';

// Define the interface based on usage in publicPsycologyProfile.tsx
interface ConsultationClass {
    _id: string;
    title?: string;
    classTitle?: string;
    description?: string;
    classDescription?: string;
    thumbnail?: string;
    classThumbnail?: string;
    category?: string;
    classCategory?: string;
    startDate?: string;
    classStartDate?: string;
    endDate?: string;
    classEndDate?: string;
    duration?: string;
    classDuration?: string;
    status?: string;
    purchaseStatus?: string;
    prices?: { session?: string };
    classPrice?: { session?: string };
    likes?: string[];
}

interface PublicPsychologySessionsProps {
    mentorId: string;
}

const getUserIdFromToken = () => {
    const token = sessionStorage.getItem('mentorAccessToken') || sessionStorage.getItem('userAccessToken') || sessionStorage.getItem('adminAccessToken');
    if (!token) return null;
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const decoded = JSON.parse(jsonPayload);
        return decoded.id || decoded.mentorId || null;
    } catch (e) {
        return null;
    }
};

const PublicPsychologySessions: React.FC<PublicPsychologySessionsProps> = ({ mentorId }) => {
    const [sessions, setSessions] = useState<ConsultationClass[]>([]);
    const [loading, setLoading] = useState(true);
    const [favorites, setFavorites] = useState<Set<string>>(new Set());
    const navigate = useNavigate();

    useEffect(() => {
        const fetchSessions = async () => {
            if (!mentorId) return;
            setLoading(true);
            try {
                const res = await fetchPublicPsychologyConsultations(mentorId);
                const data = res?.data?.data || res?.data || [];
                const sessionsList = Array.isArray(data) ? data : [];
                setSessions(sessionsList);

                // Initialize favorites set with sessions that are already liked by the current user
                const userId = getUserIdFromToken();
                if (userId) {
                    const likedSessionIds = sessionsList
                        .filter((s: any) => Array.isArray(s.likes) && s.likes.includes(userId))
                        .map((s: any) => s._id);
                    setFavorites(new Set(likedSessionIds));
                }
            } catch (error) {
                console.error("Error fetching sessions:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSessions();
    }, [mentorId]);

    const toggleFavorite = async (classId: string): Promise<void> => {
        const userId = getUserIdFromToken();
        if (!userId) {
            toast.error("Please log in to like this session");
            navigate("/login");
            return;
        }

        // Optimistically update the UI (favorites Set and sessions likes count)
        const newFavorites = new Set(favorites);
        const originalFavorites = new Set(favorites);
        const isCurrentlyLiked = newFavorites.has(classId);

        if (isCurrentlyLiked) {
            newFavorites.delete(classId);
        } else {
            newFavorites.add(classId);
        }
        setFavorites(newFavorites);

        const originalSessions = [...sessions];
        setSessions(prevSessions =>
            prevSessions.map(session => {
                if (session._id === classId) {
                    const currentLikes = Array.isArray(session.likes) ? [...session.likes] : [];
                    if (isCurrentlyLiked) {
                        const idx = currentLikes.indexOf(userId);
                        if (idx !== -1) currentLikes.splice(idx, 1);
                    } else {
                        if (!currentLikes.includes(userId)) currentLikes.push(userId);
                    }
                    return { ...session, likes: currentLikes };
                }
                return session;
            })
        );

        try {
            const res = await toggleLikeSessionApi(classId);
            if (!res.success) {
                // Revert state if failed
                setFavorites(originalFavorites);
                setSessions(originalSessions);
                toast.error(res.message || "Failed to toggle like");
            } else {
                toast.success(res.isLiked ? "Added to favorites" : "Removed from favorites");
                // Update with final data from response
                setSessions(prevSessions =>
                    prevSessions.map(session =>
                        session._id === classId
                            ? { ...session, likes: res.likes || [] }
                            : session
                    )
                );
            }
        } catch (error) {
            // Revert state if failed
            setFavorites(originalFavorites);
            setSessions(originalSessions);
            toast.error("An error occurred");
        }
    };

    const getAbsoluteImageUrl = (imageUrl: string | undefined): string => {
        if (!imageUrl) return "";
        if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) return imageUrl;
        const baseUrl = window.location.origin;
        return `${baseUrl}${imageUrl.startsWith("/") ? "" : "/"}${imageUrl}`;
    };

    const getClassTitle = (cls: ConsultationClass): string => {
        return cls.classTitle || cls.title || "Untitled Session";
    };

    const getClassDescription = (cls: ConsultationClass): string => {
        return cls.classDescription || cls.description || "No description available";
    };

    const getClassThumbnail = (cls: ConsultationClass): string => {
        return cls.classThumbnail || cls.thumbnail || "";
    };

    const getClassCategory = (cls: ConsultationClass): string => {
        return cls.classCategory || cls.category || "Psychology Session";
    };

    const getClassPrice = (cls: ConsultationClass): string => {
        if (cls.purchaseStatus === "Free" || cls.status === "Free") return "Free";
        const sessionPrice = cls.classPrice?.session || cls.prices?.session || "999";
        return `₹${sessionPrice}`;
    };

    const getPurchaseStatus = (cls: ConsultationClass): string => {
        return cls.purchaseStatus || cls.status || "Paid";
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sessions && sessions.length > 0 ? (
                sessions.map((classData: ConsultationClass, index: number) => (
                    <div
                        key={classData._id}
                        className="group relative overflow-hidden rounded-2xl border border-purple-200/80 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-violet-300"
                        style={{ animationDelay: `${index * 80}ms` }}
                    >
                        {/* Thumbnail */}
                        <div className="relative h-40 overflow-hidden bg-gradient-to-br from-violet-100 to-purple-100">
                            {getClassThumbnail(classData) ? (
                                <img
                                    src={getAbsoluteImageUrl(getClassThumbnail(classData))}
                                    alt={getClassTitle(classData)}
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                    }}
                                />
                            ) : (
                                <div className="h-full w-full flex items-center justify-center">
                                    <Brain className="w-16 h-16 text-violet-300" />
                                </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

                            {/* Price badge */}
                            <div className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-semibold tracking-wide shadow-sm backdrop-blur-sm ${getPurchaseStatus(classData) === "Free"
                                ? "bg-emerald-500 text-white"
                                : "bg-white/90 text-gray-900 border border-white/60"
                                }`}>
                                {getPurchaseStatus(classData) === "Free" ? "FREE" : getClassPrice(classData)}
                            </div>

                            {/* Fav button */}
                            <button
                                onClick={() => toggleFavorite(classData._id)}
                                className={`absolute right-3 top-3 z-10 rounded-full px-2 py-1 shadow-sm backdrop-blur-sm transition-all duration-200 flex items-center gap-1 ${favorites.has(classData._id)
                                    ? "bg-red-500 text-white scale-110 animate-pulse"
                                    : "bg-white/90 text-gray-500 hover:text-rose-500 hover:scale-110"
                                    }`}
                            >
                                <Heart className="w-3 h-3" fill={favorites.has(classData._id) ? "currentColor" : "none"} />
                                <span className="text-[10px] font-bold leading-none">
                                    {Array.isArray(classData.likes) ? classData.likes.length : 0}
                                </span>
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-4 space-y-3">
                            {/* Title */}
                            <div>
                                <div className="flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-widest text-gray-400 mb-1">
                                    <Brain className="h-3 w-3 text-violet-500" />
                                    Psychology Session
                                </div>
                                <h3 className="text-sm font-semibold leading-snug text-gray-900 line-clamp-2 group-hover:text-violet-700 transition-colors">
                                    {getClassTitle(classData)}
                                </h3>
                            </div>

                            {/* Category */}
                            <span className="inline-flex items-center rounded-full border border-violet-200 bg-violet-50 px-2.5 py-0.5 text-[10px] font-semibold text-violet-700">
                                {getClassCategory(classData)}
                            </span>

                            {/* Description */}
                            <p className="text-xs text-gray-400 leading-relaxed line-clamp-2">
                                {getClassDescription(classData)}
                            </p>

                            {/* CTA */}
                            <Link
                                to={`/psychology/session/${mentorId}/${classData._id}`}
                                className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-white shadow-sm hover:shadow-md transition-all duration-200 hover:from-violet-700 hover:to-purple-700"
                            >
                                Book Session <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                        </div>
                    </div>
                ))
            ) : (
                <div className="col-span-full text-center py-16">
                    <Brain className="w-16 h-16 text-violet-200 mx-auto mb-4" />
                    <div className="text-gray-400 text-base">No sessions available at the moment</div>
                    <p className="text-gray-300 text-sm mt-1">Check back later for new psychology sessions!</p>
                </div>
            )}
        </div>
    );
};

export default PublicPsychologySessions;
